
import { defineComponent } from "vue";
import {
  IHopDongResponseBase,
  IVatTuHopDongResponseBase,
} from "@/core/interfaces/ApiResponses";
import { HopDongService } from "@/core/services/HopDong.service";
import { VatTuService } from "@/core/services/VatTu.service";
import {
  calcLineAmount,
  formatAmount,
  formatQuantity,
  formatUnitPrice,
} from "@/core/helpers/number-format";
import { swalErrNotification } from "@/core/helpers/utils";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import useHopDong from "@/core/hooks/use-hop-dong";
import PdfViewerPage from "@/components/shared/pdf-viewer/PdfViewerPage.vue";
import PdfDocumentHeader from "@/components/shared/pdf-viewer/PdfDocumentHeader.vue";
import { generateHtmlElementPdf } from "@/components/shared/pdf-viewer/generate-html-element-pdf";
import { pickDonViPdfExtra } from "@/components/shared/pdf-viewer/pdf-document-header";
import {
  getPdfContentWidthPx,
  getPdfPrintContentStyle,
} from "@/components/shared/pdf-viewer/pdf-content.config";

export default defineComponent({
  name: "HopDongViewer",

  components: { PdfViewerPage, PdfDocumentHeader },

  setup() {
    const { moment } = useDateTime();
    const { loaiHopDong } = useHopDong();
    return { moment, loaiHopDong };
  },

  data() {
    return {
      hopDong: null as IHopDongResponseBase | null,
      vatTuItems: [] as IVatTuHopDongResponseBase[],
      pdfSource: null as string | null,
      isGenerating: false,
      tenDonVi: "",
      tenDonViCha: "",
    };
  },

  computed: {
    pdfFileName(): string {
      if (!this.hopDong) return "chi-tiet-hop-dong.pdf";
      const slug = (this.hopDong.so_hop_dong || "hop-dong")
        .replace(/\s+/g, "-")
        .toLowerCase();
      return `chi-tiet-hop-dong-${slug}.pdf`;
    },

    documentTitle(): string {
      const loai = this.loaiHopDongDisplay || "Hợp đồng";
      const so = this.hopDong?.so_hop_dong || "";
      return so ? `${loai} ${so}` : loai;
    },

    loaiHopDongDisplay(): string {
      return this.loaiHopDong(this.hopDong) || "-";
    },

    ngayKyDisplay(): string {
      if (!this.hopDong?.ngay_ky) return "-";
      return this.moment(this.hopDong.ngay_ky).format("DD/MM/YYYY");
    },

    ngayKyLongDisplay(): string {
      if (!this.hopDong?.ngay_ky) return "";
      const date = this.moment(this.hopDong.ngay_ky);
      return `Ngày ${date.date()} tháng ${date.month() + 1} năm ${date.year()}`;
    },

    ngayHetHanDisplay(): string {
      if (!this.hopDong?.ngay_het_han) return "Vô thời hạn";
      return this.moment(this.hopDong.ngay_het_han).format("DD/MM/YYYY");
    },

    isHoanThanh(): boolean {
      if (!this.hopDong) return false;

      if (
        (Object.keys(this.hopDong.remainImport).length === 0 &&
          this.hopDong.loai_hop_dong !== 2) ||
        this.hopDong.loai_hop_dong === 2
      ) {
        return false;
      }

      for (let i = 0; i < this.hopDong.remainImport.length; i++) {
        if (this.hopDong.remainImport[i].so_luong > 0) {
          return false;
        }
      }

      return true;
    },

    totalThanhTien(): number {
      return this.vatTuItems.reduce(
        (sum, item) =>
          sum + calcLineAmount(item.don_gia, item.so_luong_trong_hop_dong),
        0
      );
    },

    printPaperStyle(): Record<string, string> {
      return getPdfPrintContentStyle();
    },

    hiddenTemplateStyle(): Record<string, string> {
      return { width: `${getPdfContentWidthPx()}px` };
    },
  },

  methods: {
    calcLineAmount,
    formatAmount,
    formatQuantity,
    formatUnitPrice,

    checkboxMark(checked: boolean): string {
      return checked ? "☑" : "☐";
    },

    async loadAllVatTu(hopDongId: number): Promise<IVatTuHopDongResponseBase[]> {
      const allItems: IVatTuHopDongResponseBase[] = [];
      let page = 1;
      let total = Infinity;
      const perPage = 100;

      while (allItems.length < total) {
        const {
          data: {
            data: { data, total: totalItems },
          },
        } = await VatTuService.getVatTuByHopDongId({
          id: hopDongId,
          current_page: page,
          per_page: perPage,
        });

        total = totalItems;
        allItems.push(
          ...data.map((item) => ({
            ...item,
            id: item.id_vat_tu,
            so_luong_trong_hop_dong: item.so_luong_hop_dong,
            don_gia: item.don_gia,
          }))
        );

        if (!data.length) break;
        page += 1;
      }

      return allItems;
    },

    async loadAndGenerate() {
      const id = Number(this.$route.params.id);
      if (!id) return;

      try {
        this.isGenerating = true;
        this.pdfSource = null;

        const { data: response } = await HopDongService.fetchContractDetail(id);
        this.hopDong = response.data;
        const { tenDonVi, tenDonViCha } = pickDonViPdfExtra(response.extra);
        this.tenDonVi = tenDonVi;
        this.tenDonViCha = tenDonViCha;

        this.vatTuItems = await this.loadAllVatTu(id);

        await this.$nextTick();
        await this.$nextTick();

        await this.generatePdf();
      } catch (e) {
        swalErrNotification(e, "Có lỗi khi tải chi tiết hợp đồng");
      } finally {
        this.isGenerating = false;
      }
    },

    async generatePdf() {
      const el = (this.$refs.printArea as HTMLElement)?.querySelector(
        ".printing-paper"
      ) as HTMLElement | null;
      if (!el) return;

      this.pdfSource = await generateHtmlElementPdf(el);
    },
  },

  mounted() {
    this.loadAndGenerate();
  },
});
